import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import axiosInstance from "../../utils/axios";

// Import components
import UserCard from './components/UserCard';
import UserDataGrid from './components/UserDataGrid';
import RoleManagementPanel from './components/RoleManagementPanel';
import SessionManagementSection from './components/SessionManagementSection';
import UserDetailsDrawer from './components/UserDetailsDrawer';

const UserManagementConsole = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state for new user
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    employeeId: '',
    department: 'IT',
    role_id: '',
    status: 'Active',
    phoneNumber: '',
    location: '',
    manager: '',
    title: ''
  });

  // Form errors
  const [formErrors, setFormErrors] = useState({});

  // Role and department options
  const [roleOptions, setRoleOptions] = useState([]);

  const defaultDepartmentOptions = [
    { value: 'IT', label: 'IT', color: 'text-blue-500', bg: 'bg-blue-50' },
    { value: 'Finance', label: 'Finance', color: 'text-green-500', bg: 'bg-green-50' },
    { value: 'HR', label: 'HR', color: 'text-purple-500', bg: 'bg-purple-50' },
    { value: 'Operations', label: 'Operations', color: 'text-orange-500', bg: 'bg-orange-50' },
    { value: 'Marketing', label: 'Marketing', color: 'text-pink-500', bg: 'bg-pink-50' },
    { value: 'Sales', label: 'Sales', color: 'text-red-500', bg: 'bg-red-50' }
  ];

  const [departmentOptions, setDepartmentOptions] = useState(defaultDepartmentOptions);

  const statusOptions = [
    { value: 'Active', label: 'Active', color: 'success', icon: 'UserCheck' },
    { value: 'Pending', label: 'Pending', color: 'warning', icon: 'Clock' },
    { value: 'Suspended', label: 'Suspended', color: 'error', icon: 'UserX' }
  ];


  const isReportingManager = (candidate) => {
    const roleName =
      candidate?.role?.name ||
      candidate?.role_name ||
      candidate?.roleName ||
      '';

    return /manager|admin/i.test(String(roleName));
  };

  const reportingManagerOptions = users
    .filter((candidate) => isReportingManager(candidate))
    .sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''))
    );

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser. Please type the location manually.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );

          if (!response.ok) {
            throw new Error('Unable to determine your location.');
          }

          const data = await response.json();
          const locationParts = [
            data?.city,
            data?.locality,
            data?.principalSubdivision,
            data?.countryName,
          ].filter(Boolean);

          const uniqueParts = [...new Set(locationParts)];
          const readableLocation = uniqueParts.length
            ? uniqueParts.join(', ')
            : `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

          setNewUserForm((previous) => ({
            ...previous,
            location: readableLocation,
          }));
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
          setNewUserForm((previous) => ({
            ...previous,
            location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
          }));
          alert('Could not get the address automatically. Coordinates were added instead.');
        }
      },
      (error) => {
        console.error('Geolocation error:', error);

        if (error.code === error.PERMISSION_DENIED) {
          alert('Location permission was denied. Please allow location access or type the location manually.');
        } else if (error.code === error.TIMEOUT) {
          alert('Location request timed out. Please try again or type the location manually.');
        } else {
          alert('Unable to get your current location. Please type the location manually.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await axiosInstance.get("/departments", {
        params: { per_page: 100 }
      });
      const deptList = response?.data?.data || [];
      if (Array.isArray(deptList) && deptList.length > 0) {
        const colors = [
          { color: 'text-blue-500', bg: 'bg-blue-50' },
          { color: 'text-green-500', bg: 'bg-green-50' },
          { color: 'text-purple-500', bg: 'bg-purple-50' },
          { color: 'text-orange-500', bg: 'bg-orange-50' },
          { color: 'text-pink-500', bg: 'bg-pink-50' },
          { color: 'text-red-500', bg: 'bg-red-50' },
          { color: 'text-teal-500', bg: 'bg-teal-50' },
          { color: 'text-indigo-500', bg: 'bg-indigo-50' },
        ];
        const formatted = deptList.map((dept, index) => {
          const colorStyle = colors[index % colors.length];
          return {
            value: dept.name,
            label: dept.name,
            code: dept.code,
            ...colorStyle,
          };
        });
        setDepartmentOptions(formatted);
        setNewUserForm(prev => ({
          ...prev,
          department: prev.department && formatted.some(d => d.value === prev.department)
            ? prev.department
            : formatted[0].value
        }));
      }
    } catch (error) {
      console.error("Failed to fetch departments from department page:", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get("/roles");
      const formattedRoles = response.data.map((role) => ({
        value: role.id,
        label: role.name,
        description: role.description || '',
        icon: 'Shield'
      }));
      setRoleOptions(formattedRoles);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/admin/users");
      setUsers(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const tabs = [
    { id: 'users', label: 'User Directory', icon: 'Users' },
    { id: 'roles', label: 'Role Management', icon: 'Shield' },
    { id: 'sessions', label: 'Active Sessions', icon: 'Monitor' }
  ];

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!newUserForm.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!newUserForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(newUserForm.email)) {
      errors.email = 'Email is invalid';
    }

    if (!newUserForm.password) {
      errors.password = 'Password is required';
    } else if (newUserForm.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!newUserForm.password_confirmation) {
      errors.password_confirmation = 'Please confirm the password';
    } else if (
      newUserForm.password !== newUserForm.password_confirmation
    ) {
      errors.password_confirmation = 'Passwords do not match';
    }

    if (users.some(u => u.email === newUserForm.email)) {
      errors.email = 'Email already exists';
    }
    if (newUserForm.employeeId && users.some(
      u => u.employee_id === newUserForm.employeeId
    )) {
      errors.employeeId = 'Employee ID already exists';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const response = await axiosInstance.post("/admin/users", newUserForm);
      console.log(response.data);
      alert("User created successfully");
      await fetchUsers();
      setNewUserForm({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        employeeId: '',
        department: departmentOptions[0]?.value || 'IT',
        role_id: '',
        status: 'Active',
        phoneNumber: '',
        location: '',
        manager: '',
        title: ''
      });
      setIsCreateDrawerOpen(false);
    } catch (error) {
      console.log(error.response);
      alert(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await axiosInstance.get("/admin/users-export", {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "users.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error.response);
      alert("Export failed");
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = (filteredUsers) => {
    const allSelected = filteredUsers?.every(user => selectedUsers?.includes(user?.id));
    if (allSelected) {
      setSelectedUsers(prev => prev?.filter(id => !filteredUsers?.some(user => user?.id === id)));
    } else {
      const newSelections = filteredUsers?.map(user => user?.id);
      setSelectedUsers(prev => [...new Set([...prev, ...newSelections])]);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser({
      ...user,
      employeeId: user.employee_id,
      phoneNumber: user.phone_number,
      role_id: user.role_id,
    });
    setIsDrawerOpen(true);
  };

  const handleToggleUserStatus = async (user) => {
    const newStatus = user?.status === 'Active' ? 'Suspended' : 'Active';
    // Optimistic UI update
    setUsers(prev =>
      prev?.map(u =>
        u?.id === user?.id ? { ...u, status: newStatus } : u
      )
    );

    try {
      await axiosInstance.post(`/admin/users/${user.id}/status`, {
        status: newStatus,
      });
      fetchUsers();
    } catch (error) {
      console.warn("Status route failed, attempting update route:", error);
      try {
        await axiosInstance.post(`/admin/users/${user.id}`, {
          name: user.name,
          email: user.email,
          status: newStatus,
          department: user.department,
          employeeId: user.employee_id,
          phoneNumber: user.phone_number,
          role_id: user.role_id,
          location: user.location,
          manager: user.manager,
          title: user.title,
        });
        fetchUsers();
      } catch (err) {
        console.error("Failed to update status on server:", err);
        alert(err?.response?.data?.message || "Failed to update user status");
        fetchUsers();
      }
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await axiosInstance.get("/admin/users-sample", {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "sample-users.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error.response);
      alert("Sample download failed");
    }
  };

  const handleBulkAction = async (action) => {
    if (!selectedUsers || selectedUsers.length === 0) return;

    switch (action) {
      case 'activate':
        try {
          await axiosInstance.post('/admin/users-bulk-status', {
            user_ids: selectedUsers,
            status: 'Active',
          });
          await fetchUsers();
          alert(`Activated ${selectedUsers.length} user(s) successfully`);
        } catch (error) {
          console.error("Bulk activate failed:", error);
          // Fallback to individual updates
          try {
            await Promise.all(
              selectedUsers.map(id =>
                axiosInstance.post(`/admin/users/${id}/status`, { status: 'Active' })
              )
            );
            await fetchUsers();
            alert(`Activated ${selectedUsers.length} user(s) successfully`);
          } catch (err) {
            console.error("Fallback bulk activate failed:", err);
            alert("Failed to activate some or all selected users");
          }
        }
        break;
      case 'suspend':
        try {
          await axiosInstance.post('/admin/users-bulk-status', {
            user_ids: selectedUsers,
            status: 'Suspended',
          });
          await fetchUsers();
          alert(`Suspended ${selectedUsers.length} user(s) successfully`);
        } catch (error) {
          console.error("Bulk suspend failed:", error);
          // Fallback to individual updates
          try {
            await Promise.all(
              selectedUsers.map(id =>
                axiosInstance.post(`/admin/users/${id}/status`, { status: 'Suspended' })
              )
            );
            await fetchUsers();
            alert(`Suspended ${selectedUsers.length} user(s) successfully`);
          } catch (err) {
            console.error("Fallback bulk suspend failed:", err);
            alert("Failed to suspend some or all selected users");
          }
        }
        break;
      case 'reset-password':
        alert(`Password reset initiated for ${selectedUsers.length} user(s)`);
        break;
      default:
        break;
    }
    setSelectedUsers([]);
  };

  const handleSaveUser = async (userData) => {
    try {
      await axiosInstance.post(`/admin/users/${userData.id}`, userData);
      fetchUsers();
      setIsDrawerOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await axiosInstance.delete(`/admin/users/${id}`);
      fetchUsers();
    } catch (error) {
      console.error(error);
    }
  };

  const handleImportUsers = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axiosInstance.post("/admin/users-import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      fetchUsers();
      alert("Users imported successfully");
    } catch (error) {
      console.error(error);
    }
  };

  const handleViewUserDetails = (user) => {
    setSelectedUser({
      ...user,
      employeeId: user.employee_id,
      phoneNumber: user.phone_number,
      role_id: user.role_id,
    });
    setIsDrawerOpen(true);
  };

  const handleCreateRole = (roleData) => {
    fetchRoles();
  };

  const handleUpdateRole = (roleId, updates) => {
    fetchRoles();
  };

  const handleDeleteRole = (roleId) => {
    fetchRoles();
  };

  const handleTerminateSession = (sessionId) => {
    console.log('Terminating session:', sessionId);
  };

  const handleTerminateAllSessions = () => {
    console.log('Terminating all sessions');
  };

  const getTabContent = () => {
    switch (activeTab) {
      case 'users':
        return (
          <div className="space-y-6">
            {/* User Management Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">User Directory</h2>
                <p className="text-muted-foreground mt-1">
                  Manage user accounts, roles, and permissions across the organization
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    iconName="Grid3X3"
                  />
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                    iconName="List"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={handleExportUsers}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Export
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleDownloadSample}
                    iconName="FileDown"
                    iconPosition="left"
                  >
                    Sample
                  </Button>

                  <label>
                    <div>
                      <input
                        type="file"
                        id="import-users-file"
                        accept=".csv"
                        hidden
                        onChange={handleImportUsers}
                      />
                      <Button
                        variant="outline"
                        iconName="Upload"
                        iconPosition="left"
                        onClick={() =>
                          document
                            .getElementById("import-users-file")
                            .click()
                        }
                      >
                        Import
                      </Button>
                    </div>
                  </label>

                  <Button
                    variant="default"
                    onClick={() => setIsCreateDrawerOpen(true)}
                    iconName="UserPlus"
                    iconPosition="left"
                  >
                    Add User
                  </Button>
                </div>
              </div>
            </div>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={24} className="text-success" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-card-foreground">
                      {users?.filter(u => u?.status === 'Active')?.length}
                    </div>
                    <div className="text-sm text-muted-foreground">Active Users</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="UserX" size={24} className="text-warning" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-card-foreground">
                      {users?.filter(u => u?.status === 'Suspended')?.length}
                    </div>
                    <div className="text-sm text-muted-foreground">Suspended</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Clock" size={24} className="text-accent" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-card-foreground">
                      {users?.filter(u => u?.status === 'Pending')?.length}
                    </div>
                    <div className="text-sm text-muted-foreground">Pending</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={24} className="text-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-card-foreground">
                      {roleOptions?.length || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">System Roles</div>
                  </div>
                </div>
              </div>
            </div>
            {/* User Content */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {users?.map((user) => (
                  <UserCard
                    key={user?.id}
                    user={user}
                    onEdit={handleEditUser}
                    onToggleStatus={handleToggleUserStatus}
                    onViewDetails={handleViewUserDetails}
                    onDelete={handleDeleteUser}
                  />
                ))}
              </div>
            ) : (
              <UserDataGrid
                users={users}
                onEdit={handleEditUser}
                onToggleStatus={handleToggleUserStatus}
                onBulkAction={handleBulkAction}
                selectedUsers={selectedUsers}
                onUserSelect={handleUserSelect}
                onSelectAll={handleSelectAll}
                departmentOptions={departmentOptions}
              />
            )}
          </div>
        );

      case 'roles':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">Role Management</h2>
              <p className="text-muted-foreground mt-1">
                Configure system roles and permissions for access control
              </p>
            </div>
            <RoleManagementPanel
              onCreateRole={handleCreateRole}
              onUpdateRole={handleUpdateRole}
              onDeleteRole={handleDeleteRole}
            />
          </div>
        );

      case 'sessions':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">Session Management</h2>
              <p className="text-muted-foreground mt-1">
                Monitor and manage active user sessions across the system
              </p>
            </div>
            <SessionManagementSection
              onTerminateSession={handleTerminateSession}
              onTerminateAllSessions={handleTerminateAllSessions}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="p-6">
          <Breadcrumb />
          
          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {getTabContent()}
        </div>
      </main>
      
      {/* User Details Drawer (Edit) */}
      <UserDetailsDrawer
        user={selectedUser}
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
        roleOptions={roleOptions}
        departmentOptions={departmentOptions}
        users={users}
      />

      {/* Create User Drawer - Right Side Slide-out Panel */}
      {isCreateDrawerOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setIsCreateDrawerOpen(false)}
          />
          
          {/* Drawer Panel */}
          <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-card shadow-2xl z-50 transform transition-transform duration-300 ease-in-out animate-slide-in-right">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-6 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="UserPlus" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-card-foreground">Add New User</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create a new user account with role-based permissions
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsCreateDrawerOpen(false)}
                className="text-muted-foreground hover:text-card-foreground transition-colors p-2 hover:bg-muted rounded-lg"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="overflow-y-auto h-[calc(100%-140px)]">
              <div className="p-6 space-y-6">
                {/* Basic Information Section */}
                <div>
                  <h4 className="text-sm font-semibold text-card-foreground mb-4 flex items-center">
                    <Icon name="User" size={16} className="mr-2 text-primary" />
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        Full Name <span className="text-error">*</span>
                      </label>
                      <Input
                        value={newUserForm.name}
                        onChange={(e) => setNewUserForm({...newUserForm, name: e.target.value})}
                        placeholder="Enter full name"
                        error={formErrors.name}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        Email Address <span className="text-error">*</span>
                      </label>
                      <Input
                        type="email"
                        value={newUserForm.email}
                        onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                        placeholder="user@company.com"
                        error={formErrors.email}
                        className="w-full"
                      />
                    </div>
                    {/* Password */}

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        Password <span className="text-error">*</span>
                      </label>

                      <Input
                        type="password"
                        value={newUserForm.password}
                        onChange={(e) =>
                          setNewUserForm({
                            ...newUserForm,
                            password: e.target.value
                          })
                        }
                        placeholder="Enter password"
                        error={formErrors.password}
                        className="w-full"
                      />

                      <p className="mt-1 text-xs text-muted-foreground">
                        Password must be at least 8 characters.
                      </p>
                    </div>

                    {/* Confirm Password */}

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        Confirm Password <span className="text-error">*</span>
                      </label>

                      <Input
                        type="password"
                        value={newUserForm.password_confirmation}
                        onChange={(e) =>
                          setNewUserForm({
                            ...newUserForm,
                            password_confirmation: e.target.value
                          })
                        }
                        placeholder="Confirm password"
                        error={formErrors.password_confirmation}
                        className="w-full"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">
                          Employee ID
                        </label>
                        <Input
                          value={newUserForm.employeeId}
                          onChange={(e) => setNewUserForm({...newUserForm, employeeId: e.target.value})}
                          placeholder={`EMP${String(users.length + 1).padStart(3, '0')}`}
                          error={formErrors.employeeId}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">
                          Job Title
                        </label>
                        <Input
                          value={newUserForm.title}
                          onChange={(e) => setNewUserForm({...newUserForm, title: e.target.value})}
                          placeholder="e.g., Senior Developer"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Department & Role Section */}
                <div>
                  <h4 className="text-sm font-semibold text-card-foreground mb-4 flex items-center">
                    <Icon name="Briefcase" size={16} className="mr-2 text-primary" />
                    Organization
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        Department
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {departmentOptions.map((dept) => (
                          <button
                            key={dept.value}
                            type="button"
                            onClick={() => setNewUserForm({...newUserForm, department: dept.value})}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all ${
                              newUserForm.department === dept.value
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-border hover:border-primary/50 text-card-foreground'
                            }`}
                          >
                            <Icon name="Building2" size={14} className={dept.color} />
                            <span className="text-sm">{dept.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        Role
                      </label>
                      <div className="space-y-2">
                        {roleOptions.map((role) => (
                          <button
                            key={role.value}
                            type="button"
                            onClick={() => setNewUserForm({...newUserForm, role_id: role.value})}
                            className={`flex items-center justify-between w-full px-3 py-3 rounded-lg border transition-all ${
                              newUserForm.role_id === role.value
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <Icon 
                                name={role.icon} 
                                size={18} 
                                className={newUserForm.role_id === role.value ? 'text-primary' : 'text-muted-foreground'} 
                              />
                              <div className="text-left">
                                <div className={`text-sm font-medium ${
                                  newUserForm.role_id === role.value ? 'text-primary' : 'text-card-foreground'
                                }`}>
                                  {role.label}
                                </div>
                                <div className="text-xs text-muted-foreground">{role.description}</div>
                              </div>
                            </div>
                            {newUserForm.role_id === role.value && (
                              <Icon name="Check" size={16} className="text-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-sm font-semibold text-card-foreground mb-4 flex items-center">
                    <Icon name="Phone" size={16} className="mr-2 text-primary" />
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        Phone Number
                      </label>
                      <Input
                        value={newUserForm.phoneNumber}
                        onChange={(e) => setNewUserForm({...newUserForm, phoneNumber: e.target.value})}
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        Location
                      </label>
                      <div className="flex gap-2">
                        <Input
                          value={newUserForm.location}
                          onChange={(e) =>
                            setNewUserForm({ ...newUserForm, location: e.target.value })
                          }
                          placeholder="Type location or use current location"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGetCurrentLocation}
                          iconName="MapPin"
                          iconPosition="left"
                        >
                          Use Location
                        </Button>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        You can type any location manually or automatically fetch your current location.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-2">
                        Reporting Manager
                      </label>
                      <select
                        value={newUserForm.manager}
                        onChange={(e) =>
                          setNewUserForm({ ...newUserForm, manager: e.target.value })
                        }
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-card-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth"
                      >
                        <option value="">Select reporting manager</option>
                        {reportingManagerOptions.map((manager) => (
                          <option key={manager.id} value={manager.name}>
                            {manager.name} — {manager?.role?.name || manager?.role_name || 'Manager'}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Only users with Manager/Admin roles are available as reporting managers.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Account Status */}
                <div>
                  <h4 className="text-sm font-semibold text-card-foreground mb-4 flex items-center">
                    <Icon name="Shield" size={16} className="mr-2 text-primary" />
                    Account Status
                  </h4>
                  <div className="flex space-x-3">
                    {statusOptions.map((status) => (
                      <button
                        key={status.value}
                        type="button"
                        onClick={() => setNewUserForm({...newUserForm, status: status.value})}
                        className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border transition-all ${
                          newUserForm.status === status.value
                            ? `border-${status.color} bg-${status.color}/5 text-${status.color}`
                            : 'border-border hover:border-primary/50 text-card-foreground'
                        }`}
                      >
                        <Icon name={status.icon} size={16} />
                        <span className="text-sm font-medium">{status.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Additional Info Note */}
                <div className="p-4 bg-muted/20 rounded-lg border border-border">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-card-foreground">Note:</span> New users will receive a welcome email with instructions to set up their password. Role-based permissions will be applied immediately upon account creation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border bg-muted/10">
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="ghost"
                  onClick={() => setIsCreateDrawerOpen(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleCreateUser}
                  disabled={isSubmitting || !newUserForm.name || !newUserForm.email}
                  iconName={isSubmitting ? "Loader2" : "UserPlus"}
                  iconPosition="left"
                  className={isSubmitting ? "opacity-70 cursor-not-allowed" : ""}
                >
                  {isSubmitting ? "Creating User..." : "Create User"}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Footer */}
      <footer className={`bg-card border-t border-border py-4 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="px-6">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center space-x-4">
              <span>© {new Date()?.getFullYear()} AssetTracker Pro</span>
              <span>•</span>
              <span>User Management Console</span>
            </div>
            <div className="flex items-center space-x-4">
              <span>Last updated: {new Date().toLocaleDateString()}</span>
              <span>•</span>
              <span>{users?.length} total users</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserManagementConsole;